<template>
  <div>
    <sweet-modal
      ref="settingsModal"
      class="instagram-feed-settings-modal"
      width="560px"
      :title="$t('ct-instagram-settings-title')"
    >
      <div class="ig-settings">
        <div class="ig-settings-group">
          <label class="ig-settings-label">{{ $t('ct-instagram-layout') }}</label>
          <div class="ig-layout-picker">
            <button
              v-for="layout in layoutOptions"
              :key="layout.value"
              type="button"
              class="ig-layout-picker__item"
              :class="{ 'is-active': draft.feedLayout === layout.value }"
              @click="draft.feedLayout = layout.value"
            >
              <span class="ig-layout-icon" :class="'ig-layout-icon--' + layout.value" aria-hidden="true">
                <span v-for="n in layout.cells" :key="n"></span>
              </span>
              <span class="ig-layout-picker__label">{{ layout.label }}</span>
            </button>
          </div>
        </div>

        <section class="ig-settings-card">
          <div class="ig-settings-row">
            <div>
              <h4>{{ $t('ct-instagram-autoplay-videos') }}</h4>
              <p>{{ $t('ct-instagram-autoplay-videos-help') }}</p>
            </div>
            <label class="ig-switch">
              <input v-model="draft.autoplayVideos" type="checkbox">
              <span class="ig-switch__slider"></span>
            </label>
          </div>
          <div class="ig-settings-row">
            <div>
              <h4>{{ $t('ct-instagram-show-media-icon') }}</h4>
              <p>{{ $t('ct-instagram-show-media-icon-help') }}</p>
            </div>
            <label class="ig-switch">
              <input v-model="draft.showMediaIcon" type="checkbox">
              <span class="ig-switch__slider"></span>
            </label>
          </div>
        </section>

        <section class="ig-settings-card">
          <div class="ig-settings-row">
            <div>
              <h4>{{ $t('ct-instagram-enable-hover') }}</h4>
              <p>{{ $t('ct-instagram-enable-hover-help') }}</p>
            </div>
            <label class="ig-switch">
              <input v-model="draft.hoverEffectsEnabled" type="checkbox">
              <span class="ig-switch__slider"></span>
            </label>
          </div>
        </section>

        <p class="ig-settings-hint">{{ $t('ct-instagram-hover-hint') }}</p>
        <section class="ig-settings-card" :class="{ 'is-disabled': !draft.hoverEffectsEnabled }">
          <div class="ig-settings-row">
            <div>
              <h4>{{ $t('ct-instagram-show-logo') }}</h4>
              <p>{{ $t('ct-instagram-show-logo-help') }}</p>
            </div>
            <label class="ig-switch">
              <input v-model="draft.showInstagramLogo" type="checkbox" :disabled="!draft.hoverEffectsEnabled">
              <span class="ig-switch__slider"></span>
            </label>
          </div>
          <div class="ig-settings-row">
            <div>
              <h4>{{ $t('ct-instagram-show-caption') }}</h4>
              <p>{{ $t('ct-instagram-show-caption-help') }}</p>
            </div>
            <label class="ig-switch">
              <input v-model="draft.showCaption" type="checkbox" :disabled="!draft.hoverEffectsEnabled">
              <span class="ig-switch__slider"></span>
            </label>
          </div>
        </section>

        <section class="ig-settings-card">
          <div class="ig-settings-row">
            <div>
              <h4>{{ $t('ct-instagram-show-like-count') }}</h4>
              <p>{{ $t('ct-instagram-show-like-count-help') }}</p>
            </div>
            <label class="ig-switch">
              <input v-model="draft.showLikes" type="checkbox">
              <span class="ig-switch__slider"></span>
            </label>
          </div>
          <div class="ig-settings-row">
            <div>
              <h4>{{ $t('ct-instagram-show-comment-count') }}</h4>
              <p>{{ $t('ct-instagram-show-comment-count-help') }}</p>
            </div>
            <label class="ig-switch">
              <input v-model="draft.showComments" type="checkbox">
              <span class="ig-switch__slider"></span>
            </label>
          </div>
          <div class="ig-settings-row">
            <div>
              <h4>{{ $t('ct-instagram-show-branding') }}</h4>
              <p>{{ $t('ct-instagram-show-branding-help') }}</p>
            </div>
            <label class="ig-switch">
              <input v-model="draft.showBranding" type="checkbox">
              <span class="ig-switch__slider"></span>
            </label>
          </div>
        </section>

        <section class="ig-settings-card">
          <div class="ig-settings-row">
            <div>
              <h4>{{ $t('ct-instagram-open-new-tab') }}</h4>
              <p>{{ $t('ct-instagram-open-new-tab-help') }}</p>
            </div>
            <label class="ig-switch">
              <input v-model="draft.openLinksInNewTab" type="checkbox">
              <span class="ig-switch__slider"></span>
            </label>
          </div>
        </section>
      </div>

      <button slot="button" type="button" class="btn btn-link ig-settings-cancel" @click="cancelSettings">
        {{ $t('ct-cancel') }}
      </button>
      <button slot="button" type="button" class="btn btn-success" @click="saveSettings">
        <i class="svg-icon svgi-save"></i>
        {{ $t('ct-save') }}
      </button>
    </sweet-modal>

    <sweet-modal
      ref="successModal"
      class="instagram-feed-status-modal"
      width="480px"
      :hide-close-button="false"
    >
      <div class="instagram-feed-modal__body">
        <div class="instagram-feed-modal__icon instagram-feed-modal__icon--success">
          <span>✓</span>
        </div>
        <h3 class="instagram-feed-modal__title">
          {{ $t('ct-instagram-connected-ok') }}
        </h3>
        <div v-if="profile" class="instagram-feed-modal__account">
          <img
            v-if="profile.profilePictureUrl"
            class="instagram-feed-modal__avatar"
            :src="profile.profilePictureUrl"
            :alt="profile.username"
          />
          <div v-else class="instagram-feed-modal__avatar instagram-feed-modal__avatar--placeholder"></div>
          <span class="instagram-feed-modal__username">@{{ profile.username }}</span>
          <button type="button" class="instagram-feed-modal__disconnect" @click="onDisconnect">
            {{ $t('ct-instagram-disconnect') }}
          </button>
        </div>
      </div>
    </sweet-modal>

    <sweet-modal
      ref="selectAccountModal"
      class="instagram-feed-status-modal instagram-feed-select-modal"
      width="480px"
      :hide-close-button="false"
    >
      <div class="instagram-feed-modal__body instagram-feed-modal__body--select">
        <div class="instagram-feed-modal__icon instagram-feed-modal__icon--success">
          <span>✓</span>
        </div>
        <h3 class="instagram-feed-modal__title">
          {{ $t('ct-instagram-choose-account') }}
        </h3>
        <p class="instagram-feed-modal__message">
          {{ $t('ct-instagram-choose-account-help') }}
        </p>
        <div class="instagram-feed-modal__accounts">
          <button
            v-for="account in accounts"
            :key="account.ig_user_id"
            type="button"
            class="instagram-feed-modal__account instagram-feed-modal__account--selectable"
            :class="{ 'is-selected': selectedIgUserId === account.ig_user_id }"
            @click="selectedIgUserId = account.ig_user_id"
          >
            <img
              v-if="account.profile_picture_url"
              class="instagram-feed-modal__avatar"
              :src="account.profile_picture_url"
              :alt="account.username"
            />
            <div v-else class="instagram-feed-modal__avatar instagram-feed-modal__avatar--placeholder"></div>
            <span class="instagram-feed-modal__username">
              @{{ account.username || account.ig_user_id }}
              <small v-if="account.page_name">{{ account.page_name }}</small>
            </span>
          </button>
        </div>
        <button
          type="button"
          class="btn btn-primary instagram-feed-modal__connect"
          :disabled="!selectedIgUserId || selectingAccount"
          @click="confirmSelectAccount"
        >
          {{ selectingAccount ? $t('ct-instagram-connecting') : $t('ct-instagram-connect-account') }}
        </button>
      </div>
    </sweet-modal>

    <sweet-modal ref="tokenExpiredModal" class="instagram-feed-status-modal" width="480px">
      <div class="instagram-feed-modal__body">
        <div class="instagram-feed-modal__icon instagram-feed-modal__icon--warning">!</div>
        <h3 class="instagram-feed-modal__title">
          {{ $t('ct-instagram-token-expired') }}
        </h3>
        <p class="instagram-feed-modal__message">
          {{ $t('ct-instagram-token-expired-help') }}
        </p>
        <button type="button" class="btn btn-primary btn-block" @click="onReconnect">
          {{ $t('ct-instagram-reconnect') }}
        </button>
      </div>
    </sweet-modal>

    <sweet-modal ref="unknownErrorModal" class="instagram-feed-status-modal" width="480px">
      <div class="instagram-feed-modal__body">
        <div class="instagram-feed-modal__icon instagram-feed-modal__icon--warning">!</div>
        <h3 class="instagram-feed-modal__title">
          {{ $t('ct-instagram-unknown-error') }}
        </h3>
        <p class="instagram-feed-modal__message">
          {{ errorMessage || $t('ct-instagram-unknown-error-help') }}
        </p>
        <button type="button" class="btn btn-secondary btn-block" @click="closeUnknownError">
          {{ $t('ct-instagram-try-again') }}
        </button>
      </div>
    </sweet-modal>
  </div>
</template>

<script>
import { SweetModal } from 'sweet-modal-vue';
import { applyFeedLayoutDefaults, resolveLayoutFamily } from './layoutPresets';

function boolOrDefault(value, fallback) {
  return typeof value === 'undefined' ? fallback : !!value;
}

export default {
  name: 'InstagramFeedConfigModals',
  components: {
    SweetModal,
  },
  data() {
    return {
      profile: null,
      errorMessage: '',
      accounts: [],
      selectedIgUserId: '',
      selectingAccount: false,
      onReconnectCallback: null,
      onDisconnectCallback: null,
      onSelectAccountCallback: null,
      onSaveSettingsCallback: null,
      sourceConfig: {},
      draft: {
        feedLayout: 'grid',
        autoplayVideos: true,
        showMediaIcon: true,
        hoverEffectsEnabled: true,
        showInstagramLogo: true,
        showLikes: true,
        showComments: true,
        showCaption: true,
        showBranding: true,
        openLinksInNewTab: true,
      },
      layoutOptions: [
        { value: 'grid', label: this.$t('ct-grid'), cells: 9 },
        { value: 'slider', label: this.$t('ct-instagram-layout-slider'), cells: 4 },
        { value: 'list', label: this.$t('ct-instagram-layout-list'), cells: 3 },
        { value: 'masonry', label: this.$t('ct-instagram-layout-masonry'), cells: 6 },
      ],
    };
  },
  mounted() {
    // Keep overlays at page level (escape element overflow/transform stacking context).
    if (this.$el && this.$el.parentNode !== document.body) {
      document.body.appendChild(this.$el);
    }
  },
  beforeDestroy() {
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
  methods: {
    openSettings(config, onSave) {
      this.sourceConfig = config || {};
      this.onSaveSettingsCallback = onSave || null;
      this.draft = this.normalizeDraft(this.sourceConfig);
      if (this.$refs.settingsModal) {
        this.$refs.settingsModal.open();
      }
    },
    syncDraftFromConfig(config) {
      const modal = this.$refs.settingsModal;
      if (!modal || !modal.visible) {
        return;
      }
      this.sourceConfig = config || {};
      const next = this.normalizeDraft(this.sourceConfig);
      this.draft.feedLayout = next.feedLayout;
      this.draft.hoverEffectsEnabled = next.hoverEffectsEnabled;
      this.draft.showLikes = next.showLikes;
      this.draft.showComments = next.showComments;
    },
    normalizeDraft(config) {
      const c = config || {};
      const hover = typeof c.hoverEffectsEnabled !== 'undefined'
        ? !!c.hoverEffectsEnabled
        : c.showEngagement !== false;
      return {
        feedLayout: resolveLayoutFamily(c.feedLayout),
        autoplayVideos: boolOrDefault(c.autoplayVideos, true),
        showMediaIcon: boolOrDefault(c.showMediaIcon, true),
        hoverEffectsEnabled: hover,
        showInstagramLogo: boolOrDefault(c.showInstagramLogo, true),
        showLikes: c.showLikes !== false,
        showComments: c.showComments !== false,
        showCaption: boolOrDefault(c.showCaption, true),
        showBranding: boolOrDefault(c.showBranding, true),
        openLinksInNewTab: boolOrDefault(c.openLinksInNewTab, true),
      };
    },
    cancelSettings() {
      this.closeSettings();
    },
    saveSettings() {
      const merged = Object.assign({}, this.sourceConfig, this.draft, {
        showEngagement: !!this.draft.hoverEffectsEnabled,
      });
      const patch = applyFeedLayoutDefaults(this.draft.feedLayout, merged);
      if (typeof this.onSaveSettingsCallback === 'function') {
        this.onSaveSettingsCallback({
          feedLayout: patch.feedLayout,
          sliderVisibleCount: patch.sliderVisibleCount,
          autoplayVideos: this.draft.autoplayVideos,
          showMediaIcon: this.draft.showMediaIcon,
          hoverEffectsEnabled: this.draft.hoverEffectsEnabled,
          showEngagement: !!this.draft.hoverEffectsEnabled,
          showInstagramLogo: this.draft.showInstagramLogo,
          showLikes: this.draft.showLikes,
          showComments: this.draft.showComments,
          showCaption: this.draft.showCaption,
          showBranding: this.draft.showBranding,
          openLinksInNewTab: this.draft.openLinksInNewTab,
        });
      }
      this.closeSettings();
    },
    closeSettings() {
      if (this.$refs.settingsModal) {
        this.$refs.settingsModal.close();
      }
    },
    openSuccess(profile, onDisconnect) {
      this.profile = profile || null;
      this.onDisconnectCallback = onDisconnect || null;
      if (this.$refs.successModal) {
        this.$refs.successModal.open();
      }
    },
    openSelectAccount(accounts, onSelect) {
      this.accounts = accounts || [];
      this.selectedIgUserId = this.accounts.length === 1 ? this.accounts[0].ig_user_id : '';
      this.selectingAccount = false;
      this.onSelectAccountCallback = onSelect || null;
      if (this.$refs.selectAccountModal) {
        this.$refs.selectAccountModal.open();
      }
    },
    confirmSelectAccount() {
      const selected = this.accounts.find((account) => account.ig_user_id === this.selectedIgUserId);
      if (!selected || this.selectingAccount) {
        return;
      }
      this.selectingAccount = true;
      if (this.$refs.selectAccountModal) {
        this.$refs.selectAccountModal.close();
      }
      if (typeof this.onSelectAccountCallback === 'function') {
        this.onSelectAccountCallback(selected);
      }
    },
    openTokenExpired(onReconnect) {
      this.onReconnectCallback = onReconnect || null;
      if (this.$refs.tokenExpiredModal) {
        this.$refs.tokenExpiredModal.open();
      }
    },
    openUnknownError(message, onRetry) {
      this.errorMessage = message || '';
      this.onReconnectCallback = onRetry || null;
      if (this.$refs.unknownErrorModal) {
        this.$refs.unknownErrorModal.open();
      }
    },
    onDisconnect() {
      if (this.$refs.successModal) {
        this.$refs.successModal.close();
      }
      if (typeof this.onDisconnectCallback === 'function') {
        this.onDisconnectCallback();
      }
    },
    onReconnect() {
      if (this.$refs.tokenExpiredModal) {
        this.$refs.tokenExpiredModal.close();
      }
      if (typeof this.onReconnectCallback === 'function') {
        this.onReconnectCallback();
      }
    },
    closeUnknownError() {
      if (this.$refs.unknownErrorModal) {
        this.$refs.unknownErrorModal.close();
      }
      if (typeof this.onReconnectCallback === 'function') {
        this.onReconnectCallback();
      }
    },
  },
};
</script>

<style scoped>
.instagram-feed-modal__body {
  text-align: center;
  padding: 12px 8px 4px;
}
.instagram-feed-modal__icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 16px;
}
.instagram-feed-modal__icon--success {
  background: #22c55e;
  color: #fff;
}
.instagram-feed-modal__icon--warning {
  background: #fee2e2;
  color: #dc2626;
  border: 2px solid #fecaca;
}
.instagram-feed-modal__title {
  margin: 0 0 12px;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
}
.instagram-feed-modal__message {
  margin: 0 0 20px;
  color: #64748b;
  line-height: 1.5;
}
.instagram-feed-modal__body--select {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.instagram-feed-modal__accounts {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  margin: 0 auto 16px;
  max-height: 280px;
  overflow-y: auto;
  box-sizing: border-box;
}
.instagram-feed-modal__account {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 12px;
  text-align: left;
  box-sizing: border-box;
}
.instagram-feed-modal__account--selectable {
  width: 100%;
  margin: 0;
  border: 1px solid transparent;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  font: inherit;
}
.instagram-feed-modal__connect {
  display: block;
  width: 100%;
  margin: 0;
  box-sizing: border-box;
}
.instagram-feed-modal__account--selectable:hover {
  border-color: #cbd5e1;
}
.instagram-feed-modal__account--selectable.is-selected {
  background: #eff6ff;
  border-color: #2d6df6;
}
.instagram-feed-modal__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
.instagram-feed-modal__avatar--placeholder {
  background: #e2e8f0;
}
.instagram-feed-modal__username {
  flex: 1;
  font-weight: 600;
  color: #334155;
  min-width: 0;
}
.instagram-feed-modal__username small {
  display: block;
  font-weight: 400;
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.instagram-feed-modal__disconnect {
  border: 0;
  border-radius: 8px;
  background: #ef4444;
  color: #fff;
  font-weight: 600;
  padding: 8px 14px;
  cursor: pointer;
}
.ig-settings {
  text-align: left;
  padding-top: 4px;
}
.ig-settings-group {
  margin-bottom: 16px;
}
.ig-settings-label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}
.ig-layout-picker {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}
.ig-layout-picker__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 72px;
  padding: 10px 4px 8px;
  border: 1px solid #d5d9e2;
  border-radius: 8px;
  background: #fff;
  color: #6b7288;
  font: inherit;
  line-height: 1;
  overflow: visible;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
}
.ig-layout-picker__item:hover:not(.is-active) {
  border-color: #2d6df6;
  color: #2d6df6;
}
.ig-layout-picker__item.is-active {
  background: #2d6df6;
  border-color: #2d6df6;
  color: #fff;
}
.ig-layout-picker__label {
  font-size: 11px;
  line-height: 1.2;
  font-weight: 500;
}
.ig-layout-icon {
  display: grid;
  gap: 2px;
  width: 24px;
  height: 20px;
  flex-shrink: 0;
  overflow: visible;
}
.ig-layout-icon span {
  display: block;
  background: currentColor;
  border-radius: 1px;
}
.ig-layout-icon--grid {
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
}
.ig-layout-icon--slider {
  width: 24px;
  height: 10px;
  align-self: center;
  grid-template-columns: repeat(4, 1fr);
}
.ig-layout-icon--list {
  width: 12px;
  height: 20px;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(3, 1fr);
}
.ig-layout-icon--masonry {
  width: 24px;
  height: 20px;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
}
.ig-layout-icon--masonry span:nth-child(1) { grid-column: 1; grid-row: 1 / 3; }
.ig-layout-icon--masonry span:nth-child(2) { grid-column: 2; grid-row: 1; }
.ig-layout-icon--masonry span:nth-child(3) { grid-column: 3; grid-row: 1 / 3; }
.ig-layout-icon--masonry span:nth-child(4) { grid-column: 1; grid-row: 3; }
.ig-layout-icon--masonry span:nth-child(5) { grid-column: 2; grid-row: 2 / 4; }
.ig-layout-icon--masonry span:nth-child(6) { grid-column: 3; grid-row: 3; }
.ig-settings-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 4px 16px;
  margin-bottom: 12px;
  background: #fff;
}
.ig-settings-card.is-disabled {
  opacity: 0.55;
}
.ig-settings-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
}
.ig-settings-row + .ig-settings-row {
  border-top: 1px solid #f1f5f9;
}
.ig-settings-row h4 {
  margin: 0 0 2px;
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}
.ig-settings-row p {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.4;
}
.ig-settings-hint {
  margin: 0 0 8px;
  font-size: 12px;
  color: #94a3b8;
}
.ig-switch {
  position: relative;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}
.ig-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.ig-switch__slider {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: #cbd5e1;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.2s;
}
.ig-switch__slider:before {
  content: '';
  position: absolute;
  height: 18px;
  width: 18px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
}
.ig-switch input:checked + .ig-switch__slider {
  background: #2d6df6;
}
.ig-switch input:checked + .ig-switch__slider:before {
  transform: translateX(20px);
}
.ig-switch input:disabled + .ig-switch__slider {
  cursor: default;
}
</style>

<style lang="scss">
/* sweet-modal is teleported and applies .btn { margin-left: 18px } globally */
.instagram-feed-select-modal {
  .sweet-content {
    text-align: center;
  }

  .instagram-feed-modal__body--select {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .instagram-feed-modal__accounts,
  .instagram-feed-modal__connect {
    width: 100%;
    max-width: 100%;
    margin-left: 0 !important;
    margin-right: 0 !important;
    box-sizing: border-box;
  }

  .btn {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
}

.instagram-feed-settings-modal {
  .sweet-box {
    overflow: visible;
  }

  /* sweet-modal centers .sweet-content with align-items: center, so scrolling it
     hides the top of the content instead of the bottom. Scroll the inner wrapper. */
  .sweet-content {
    max-height: none !important;
    padding: 20px 24px 12px !important;
    box-sizing: border-box;
  }

  .sweet-content-content {
    max-height: 68vh;
    overflow: hidden auto;
  }

  .ig-layout-picker__item {
    overflow: visible !important;
    line-height: 1;
  }

  .ig-layout-icon {
    overflow: visible;
  }

  .sweet-buttons {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center;
    width: 100%;
    padding: 8px 20px 12px !important;
  }

  .btn {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .ig-settings-cancel {
    color: #64748b !important;
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    min-height: auto !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/editor/apps/InstagramFeed/ConfigModals.vue